import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import useDisableEstablishment from "../../hooks/useDisableEstablishment";
import useEnableEstablishment from "../../hooks/useEnableEstablishment";
import { establishmentDisableSchema, EstablishmentDisableValidationSchema, establishmentEnableSchema, EstablishmentEnableValidationSchema } from "../../schemas/enableDisableEstablishment.schema";

interface DisableEnableEstablishmentProps {
  establishment?: EstablishmentCompanyData;
  disableButtons?: boolean;
}

const DisableEnableEstablishment = ({ establishment, disableButtons }: DisableEnableEstablishmentProps) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [blockModalIsOpen, setBlockModalIsOpen] = useState(false);
  const [unblockModalIsOpen, setUnblockModalIsOpen] = useState(false);

  const { mutate: blockEstablishment } = useDisableEstablishment({
    establishmentId: establishment?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishment?.id] });
      blockFormReset();
      setBlockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: blockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { mutate: unblockEstablishment } = useEnableEstablishment({
    establishmentId: establishment?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishment?.id] });
      unblockFormReset();
      setUnblockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: unblockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isDisabled = establishment?.status === 'disabled';

  const {
    register: blockFormRegister,
    reset: blockFormReset,
    setError: blockFormSetError,
    handleSubmit: blockFormHandleSubmit,
    formState: { errors: blockFormErrors },
  } = useForm<EstablishmentDisableValidationSchema>({
    resolver: zodResolver(establishmentDisableSchema),
  });

  const {
    register: unblockFormRegister,
    reset: unblockFormReset,
    setError: unblockFormSetError,
    handleSubmit: unblockFormHandleSubmit,
    formState: { errors: unblockFormErrors },
  } = useForm<EstablishmentEnableValidationSchema>({
    resolver: zodResolver(establishmentEnableSchema),
  });

  return (
    <>
      <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isDisabled && 'bg-[#FCCDCA33]')}>
        <div className="flex items-center gap-x-2">
          <InfoIcon strokeWidth={1} className="text-yellow-600" />

          <div>
            <p className="font-bold text-[10px] leading-3">
              {!isDisabled ? (
                'Ao Desativar, o estabelecimento não poderá realizar transações e todos os usuários perderão o acesso ao sistema.'
              ) : (
                'Reative o estabelecimento para permitir que todas as transações sejam realizadas e o acesso dos usuários restabelecidos.'
              )}
            </p>

            <p className="font-light text-[10px] italic leading-3">
              {!isDisabled ? (
                'Se o estabelecimento for desativado, apenas a Tcard poderá reativar.'
              ) : (
                'Apenas a Tcard pode reativar um estabelecimento.'
              )}
            </p>
          </div>
        </div>

        {!isDisabled ? (
          <Modal
            triggerDisabled={disableButtons}
            Trigger={(
              <Button operationKlassToCheckPermission="Admin::Establishment::Companies::Disable" type="button" variant="error" disabled={disableButtons}>
                Desativar estabelecimento
              </Button>
            )}
            title="Você tem certeza?"
            subtitle="Se o estabelecimento for desativado, apenas a Tcard poderá reativar."
            subtitleClassName="mt-1 text-white-300"
            showClose={false}
            useCancellableFooter
            isOpen={blockModalIsOpen}
            setIsOpen={setBlockModalIsOpen}
            onConfirm={blockFormHandleSubmit((data) => blockEstablishment(data))}
            body={(
              <form className="flex flex-col gap-4">
                <TextField
                  className="text-white-800 opacity-80"
                  label="Motivo"
                  {...blockFormRegister('disable_reason')}
                  errorMessage={blockFormErrors.disable_reason?.message || undefined}
                />
              </form>
            )}
          />
        ) : (
          <Modal
            triggerDisabled={disableButtons}
            Trigger={(
              <Button operationKlassToCheckPermission="Admin::Establishment::Companies::Reenable" type="button" disabled={disableButtons}>
                Reativar estabelecimento
              </Button>
            )}
            title="Você tem certeza?"
            subtitle="Se o estabelecimento for reativado, todas as transações poderão ser realizadas."
            subtitleClassName="mt-1 text-white-300"
            showClose={false}
            useCancellableFooter
            isOpen={unblockModalIsOpen}
            setIsOpen={setUnblockModalIsOpen}
            onConfirm={unblockFormHandleSubmit((data) => unblockEstablishment(data))}
            body={(
              <form className="flex flex-col gap-4">
                <TextField
                  className="text-white-800 opacity-80"
                  label="Motivo"
                  {...unblockFormRegister('enable_reason')}
                  errorMessage={unblockFormErrors.enable_reason?.message || undefined}
                />
              </form>
            )}
          />
        )}
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default DisableEnableEstablishment;
